import React, { useContext, useEffect, useState, Suspense } from 'react';
import { SettingsContext } from "../../SettingsContext";
import { fetchTransactionProgress } from '../../../services/Http/Api';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import { useCurrency } from '../../../context/CurrencyProvider';

// Dynamically import ReactApexChart
const ReactApexChart = React.lazy(() => import('react-apexcharts'));

const TransactionsProgress = ({ period }) => {
    const { siteSettings } = useContext(SettingsContext);
    const { t } = useTranslation();
    const { formatResolvedAmount } = useCurrency();
    const [progressData, setProgressData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const response = await fetchTransactionProgress({ period });
                setProgressData(response);
                setLoading(false);
            } catch (err) {
                setError(err);
                setLoading(false);
            }
        };

        if (period) {
            fetchData();
        }
    }, [period]);

    if (loading) {
        return <p>{t('Transactions.TransactionsProgress.loading')}</p>;
    }

    if (error) {
        return <p>{t('Transactions.TransactionsProgress.error', { error: error.message })}</p>;
    }

    const currentPeriodTotals = progressData.totals.current_period_totals;
    const previousPeriodTotals = progressData.totals.previous_period_totals;
    const currentDemographics = progressData.demographics.current_period_demographics;
    const previousDemographics = progressData.demographics.previous_period_demographics;

    const countries = siteSettings?.getAllCountries() || [];

    const getCountryNameById = (id) => {
        const country = countries.find(country => country.id === Number(id));
        return country ? country.name : id;
    };

    const generateChartData = (metric) => {
        const isCountryMetric = metric === 'country_summary' || metric === 'residential_country_summary';
        return {
            categories: isCountryMetric
                ? currentPeriodTotals.map(item => getCountryNameById(item.name))
                : currentPeriodTotals.map(item => item.name),
            currentSeries: currentPeriodTotals.map(item => item[metric]),
            previousSeries: previousPeriodTotals.map(item => item[metric]),
        };
    };

    const generateDemographicChartData = (metric, currentData, previousData) => {
        if (!currentData || !Array.isArray(currentData) || currentData.length === 0 ||
            !previousData || !Array.isArray(previousData) ||
            !currentData[0] || !currentData[0][metric]) {
            return {
                categories: [],
                currentSeries: [],
                previousSeries: []
            };
        }

        const categories = Object.keys(currentData[0][metric] || {});
        const currentSeries = currentData.map(item => ({
            name: getCountryNameById(item.name),
            data: categories.map(category => item[metric][category] || 0)
        }));

        const previousSeries = previousData.map(item => ({
            name: getCountryNameById(item.name),
            data: categories.map(category => item[metric][category] || 0)
        }));

        return { categories, currentSeries, previousSeries };
    };

    const createChartOptions = (categories, metricLabel, moneyMetric = false) => ({
        chart: {
            type: 'bar',
            height: 350,
            toolbar: { show: false },
            fontFamily: 'Inter, system-ui, sans-serif'
        },
        colors: ['#821692', '#3fc380'],
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                borderRadius: 4
            },
        },
        dataLabels: {
            enabled: false, // Disabled to avoid overlapping, tooltips provide data
        },
        stroke: {
            show: true,
            width: 2,
            colors: ['transparent']
        },
        xaxis: {
            categories: categories,
            labels: { style: { colors: '#64748b' } },
            axisBorder: { show: false },
            axisTicks: { show: false }
        },
        yaxis: {
            title: {
                text: metricLabel,
                style: { color: '#64748b', fontWeight: 600 }
            },
            labels: {
                style: { colors: '#64748b' },
                formatter: (val) => moneyMetric
                    ? formatResolvedAmount({ eurAmount: val, options: { maximumFractionDigits: 0 } })
                    : val.toLocaleString(undefined, { maximumFractionDigits: 0 })
            }
        },
        fill: {
            opacity: 0.85
        },
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            fontSize: '13px',
            labels: { colors: '#64748b' }
        },
        tooltip: {
            y: {
                formatter: function (val) {
                    return moneyMetric
                        ? formatResolvedAmount({ eurAmount: val })
                        : val.toLocaleString();
                }
            }
        },
        title: {
            text: metricLabel,
            style: { fontSize: '16px', fontWeight: 600, color: '#1e293b' }
        }
    });

    const renderChart = (metric, metricLabel) => {
        const chartData = generateChartData(metric);
        const moneyMetric = metric === 'total_price' || metric === 'total_revenue';

        const chartSeries = [
            {
                name: `${t('Transactions.TransactionsProgress.currentPeriod')} - ${metricLabel}`,
                data: chartData.currentSeries,
            },
            {
                name: `${t('Transactions.TransactionsProgress.previousPeriod')} - ${metricLabel}`,
                data: chartData.previousSeries,
            },
        ];

        return (
            <Suspense fallback={<p>Loading Chart...</p>}>
                <ReactApexChart
                    key={metric}
                    options={createChartOptions(chartData.categories, metricLabel, moneyMetric)}
                    series={chartSeries}
                    type="bar"
                    height={350}
                />
            </Suspense>
        );
    };

    const renderDemographicChart = (metric, metricLabel) => {
        const chartData = generateDemographicChartData(metric, currentDemographics, previousDemographics);

        const chartSeries = [
            {
                name: `${t('Transactions.TransactionsProgress.currentPeriod')} - ${metricLabel}`,
                data: chartData.currentSeries.reduce((acc, curr) => curr.data.map((val, idx) => (acc[idx] || 0) + val), [])
            },
            {
                name: `${t('Transactions.TransactionsProgress.previousPeriod')} - ${metricLabel}`,
                data: chartData.previousSeries.reduce((acc, curr) => curr.data.map((val, idx) => (acc[idx] || 0) + val), [])
            }
        ];

        return (
            <ReactApexChart
                key={metric}
                options={createChartOptions(chartData.categories, metricLabel)}
                series={chartSeries}
                type="bar"
                height={350}
            />
        );
    };

    return (
        <Container>
            <Header>
                <Title>{t('Transactions.TransactionsProgress.title')}</Title>
                <PeriodDescription>
                    {t('Transactions.TransactionsProgress.showingDataFor', { period })}
                </PeriodDescription>
            </Header>

            {progressData && (
                <ChartsGrid>
                    <FullWidthCard>
                        <SectionTitle>{t('Transactions.TransactionsProgress.totals')}</SectionTitle>
                        <ChartStack>
                            {renderChart('total_price', t('Transactions.TransactionsProgress.totalPrice'))}
                            {renderChart('total_revenue', t('Transactions.TransactionsProgress.totalRevenue'))}
                            {renderChart('total_transactions', t('Transactions.TransactionsProgress.totalTransactions'))}
                            {renderChart('distinct_buyer_count', t('Transactions.TransactionsProgress.distinctBuyerCount'))}
                        </ChartStack>
                    </FullWidthCard>

                    <FullWidthCard>
                        <SectionTitle>{t('Transactions.TransactionsProgress.demographics')}</SectionTitle>
                        <ChartStack>
                            {renderDemographicChart('age_group_summary', t('Transactions.TransactionsProgress.ageGroups'))}
                            {renderDemographicChart('gender_summary', t('Transactions.TransactionsProgress.genderDistribution'))}
                            {renderDemographicChart('country_summary', t('Transactions.TransactionsProgress.countryDistribution'))}
                            {renderDemographicChart('city_summary', t('Transactions.TransactionsProgress.cityDistribution'))}
                            {renderDemographicChart('residential_country_summary', t('Transactions.TransactionsProgress.residentialCountryDistribution'))}
                        </ChartStack>
                    </FullWidthCard>
                </ChartsGrid>
            )}
        </Container>
    );
};

export default TransactionsProgress;

// Styled Components
const Container = styled.div``;

const Header = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
    margin-bottom: 1.5rem;

    @media (min-width: 768px) {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
    }
`;

const Title = styled.h1`
    font-size: 1.25rem;
    color: #1e293b;
    font-weight: 700;
    margin: 0;
`;

const PeriodDescription = styled.p`
    font-size: 0.875rem;
    color: #64748b;
    margin: 0;

    @media (max-width: 768px) {
        text-align: center;
    }
`;

const SectionTitle = styled.h3`
    font-size: 1rem;
    color: #1e293b;
    font-weight: 600;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 1px solid #f1f5f9;
`;

const ChartsGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
`;

const FullWidthCard = styled(StyledCard)`
    padding: 1.5rem;
    margin-bottom: 0;
`;

const ChartStack = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2.5rem;
`;
